import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@apollo/client";
import { GET_USERS_PAGED } from "src/api/users";
import { GetUsers_users as umsUser } from "src/api/generated/GetUsers";
import { ums_v_customer_users_bool_exp, ums_v_customer_users_order_by } from "src/api/generated/globalTypes";
import UserList from "src/screens/dashboard/users/user/list/UserList";
import { observer } from "mobx-react-lite";
import { UI, DropdownItem } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import classes from "./UsersOverview.module.css";
import { withRouter, RouteComponentProps } from "react-router";

enum UserSortKey {
    LAST_NAME = "1",
    FIRST_NAME = "2",
    E_MAIL_ADDRESS = "3",
    CUSTOMER_NAME = "4"
}

const ITEMS_PER_PAGE = 25;
const SEARCH_DEBOUNCE_DELAY_MS = 300;

interface GetUsersPagedData {
    users: umsUser[];
    users_aggregate: {
        aggregate: {
            count: number;
        } | null;
    };
}

interface GetUsersPagedVariables {
    limit: number;
    offset: number;
    where: ums_v_customer_users_bool_exp;
    orderBy: ums_v_customer_users_order_by[];
}

const UsersOverviewBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { navStore, uiStore } = useContext(RootStoreContext);

    const listIdentifier = "userlist";

    if (!navStore.listMap.has(listIdentifier)) {
        navStore.setListMapValue(listIdentifier, {});
    }

    const listParameters = navStore.getListMapValue(listIdentifier);

    const [searchInput, setSearchInput] = useState(listParameters?.searchQuery ?? "");
    const [searchQuery, setSearchQuery] = useState(listParameters?.searchQuery ?? "");
    const [sortValue, setSortValue] = useState(listParameters?.sortValue ?? UserSortKey.LAST_NAME);
    const [currentPage, setCurrentPage] = useState<number>(listParameters?.paginationPage ?? 1);

    const sortTypeDropdownItems: DropdownItem[] = [
        { value: UserSortKey.LAST_NAME, label: "Nachname" },
        { value: UserSortKey.FIRST_NAME, label: "Vorname" },
        { value: UserSortKey.E_MAIL_ADDRESS, label: "E-Mail-Adresse" },
        { value: UserSortKey.CUSTOMER_NAME, label: "Kunde" }
    ];

    useEffect(() => {
        navStore.setTitle("Benutzer");
        navStore.setBackbutton(null);
    }, [navStore]);

    const getOrderBy = useCallback((selectedSortValue: string): ums_v_customer_users_order_by[] => {
        switch (selectedSortValue) {
            case UserSortKey.CUSTOMER_NAME:
                return [{ customername: "asc" }, { name2: "asc" }, { name1: "asc" }];
            case UserSortKey.E_MAIL_ADDRESS:
                return [{ email: "asc" }, { name2: "asc" }, { name1: "asc" }];
            case UserSortKey.FIRST_NAME:
                return [{ name1: "asc" }, { name2: "asc" }];
            case UserSortKey.LAST_NAME:
                return [{ name2: "asc" }, { name1: "asc" }];
            default:
                return [{ name2: "asc" }, { name1: "asc" }];
        }
    }, []);

    const usersQueryVariables = useMemo<GetUsersPagedVariables>(() => {
        const normalizedSearchQuery = searchQuery.trim();
        const where =
            normalizedSearchQuery.length === 0
                ? {}
                : {
                      _or: [
                          { email: { _ilike: `%${normalizedSearchQuery}%` } },
                          { name: { _ilike: `%${normalizedSearchQuery}%` } },
                          { customername: { _ilike: `%${normalizedSearchQuery}%` } }
                      ]
                  };

        return {
            limit: ITEMS_PER_PAGE,
            offset: (currentPage - 1) * ITEMS_PER_PAGE,
            where,
            orderBy: getOrderBy(sortValue)
        };
    }, [currentPage, getOrderBy, searchQuery, sortValue]);

    const { data, previousData, loading, error, refetch } = useQuery<GetUsersPagedData, GetUsersPagedVariables>(
        GET_USERS_PAGED,
        {
        variables: usersQueryVariables,
        notifyOnNetworkStatusChange: true
        }
    );

    const handleSearchInput = useCallback(
        ({ searchQuery, sortItemValue }) => {
            const nextSearchQuery = searchQuery ?? "";
            const nextSortValue = sortItemValue ?? sortValue;

            setSearchInput(nextSearchQuery);
            setSortValue(nextSortValue);
            setCurrentPage(1);
        },
        [sortValue]
    );

    useEffect(() => {
        const updateSearchTimeout = window.setTimeout(() => {
            const currentListParameters = navStore.getListMapValue(listIdentifier);
            setSearchQuery(searchInput);
            navStore.setListMapValue(
                listIdentifier,
                currentListParameters
                    ? {
                          ...currentListParameters,
                          scrollPosition: 0,
                          searchQuery: searchInput,
                          sortValue: sortValue,
                          paginationPage: 1
                      }
                    : { scrollPosition: 0, searchQuery: searchInput, sortValue: sortValue, paginationPage: 1 }
            );
        }, SEARCH_DEBOUNCE_DELAY_MS);

        return () => window.clearTimeout(updateSearchTimeout);
    }, [listIdentifier, navStore, searchInput, sortValue]);

    useEffect(() => {
        if (uiStore.hasNewUserData) {
            refetch();
            uiStore.setHasNewUserData(false);
        }
    }, [refetch, uiStore]);

    const onChangeSelection = useCallback(
        ({ searchQuery, sortItemValue }) => {
            const nextSearchQuery = searchQuery ?? "";
            const nextSortValue = sortItemValue ?? UserSortKey.LAST_NAME;

            setSearchQuery(nextSearchQuery);
            setSortValue(nextSortValue);
            setCurrentPage(1);

            navStore.setListMapValue(
                listIdentifier,
                listParameters
                    ? { ...listParameters, sortValue: nextSortValue, searchQuery: nextSearchQuery, paginationPage: 1 }
                    : { sortValue: nextSortValue, searchQuery: nextSearchQuery, paginationPage: 1 }
            );
        },
        [navStore, listParameters]
    );

    const onPageChanged = useCallback(
        (nextPage: number) => {
            setCurrentPage(nextPage);

            navStore.setListMapValue(
                listIdentifier,
                listParameters
                    ? { ...listParameters, scrollPosition: window.pageYOffset, paginationPage: nextPage }
                    : { scrollPosition: window.pageYOffset, paginationPage: nextPage }
            );
        },
        [listParameters, navStore]
    );

    const resolvedData = data ?? previousData;

    if (loading && !resolvedData) {
        return <Screens.Loading />;
    }
    if (error) {
        return <Screens.Error message={error.message} networkError={error.networkError} />;
    }
    if (!resolvedData) {
        return <Screens.Error message={t("error.nodata")} />;
    }

    const userSortDropdown = (
        <div className={"d-flex align-items-center"}>
            <div className={classes.DropdownLabel}>
                <span>Sortieren nach:</span>
            </div>
            <UI.Dropdown
                className={classes.UserSortDropDown}
                id="user-filter"
                items={sortTypeDropdownItems}
                label={sortTypeDropdownItems[0].label}
                defaultLabel={sortTypeDropdownItems[0].label}
                onSelect={onChangeSelection}
            />
        </div>
    );

    return (
        <React.Fragment>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={12}>
                        <UI.Card>
                            <div className={classes.customerList}>
                                <UserList
                                    users={resolvedData.users}
                                    totalEntries={resolvedData.users_aggregate.aggregate?.count ?? 0}
                                    currentPage={currentPage}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    onPageChanged={onPageChanged}
                                    onSearch={handleSearchInput}
                                    onChangeSort={onChangeSelection}
                                    sortItems={sortTypeDropdownItems}
                                    searchAppendElement={userSortDropdown}
                                />
                            </div>
                        </UI.Card>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </React.Fragment>
    );
};

export const UsersOverview = withRouter(Sentry.withProfiler(observer(UsersOverviewBase)));
